// Shared resume templates + palettes. Used by:
//   • The static gallery pages (/labs/resumes, /labs/resumes/2)
//   • The data-driven builder (ResumeBuilderModal + render endpoint)
//
// Two templates only - Courier (left-sidebar) and Statement (single-column
// with mission callout). Each template renders from the same `ResumeData`
// shape and accepts a palette object that drives all colors and fonts.
//
// Density adaptation: Resume.density tells the renderer how to scale
// type and spacing. The LLM picks "rich" / "balanced" / "compact" based
// on how much content the participant has, so the resume always fills -
// and never overflows - a single 8.5×11 sheet.

import type { JSX } from "react";

// ─────────────────────────────────────────────────────────────────────────
// Resume data shape - what the LLM extracts and what templates consume
// ─────────────────────────────────────────────────────────────────────────

/** A single resume bullet. Stored as an object so we can carry an `ai`
 *  flag - true when the LLM authored it (rather than extracting it from
 *  the participant's paste). The flag is editor-only metadata; the final
 *  PDF renders both kinds identically. */
export interface ResumeBullet {
  text: string;
  ai?: boolean;
}

export interface ResumeJob {
  role: string;
  org: string;
  city: string;
  start: string;
  end: string;
  bullets: ResumeBullet[];
}

export interface ResumeEducation {
  school: string;
  degree: string;
  year: string;
  detail?: string;
}

export interface ResumeData {
  first: string;
  middle?: string;
  last: string;
  initials: string;
  city: string;
  email: string;
  phone: string;
  title: string;
  long_pitch: string;
  experience: ResumeJob[];
  education: ResumeEducation[];
  skills: string[];
  /** Density hint produced by the LLM - controls type sizes and gaps so
   *  the resume always reads as one full page regardless of how much
   *  content the participant has. */
  density?: "rich" | "balanced" | "compact";
}

// ─────────────────────────────────────────────────────────────────────────
// Palettes
// ─────────────────────────────────────────────────────────────────────────

export interface CourierPalette {
  id: string;
  name: string;
  blurb: string;
  railBg: string;
  railText: string;
  railSub: string;
  railAccent: string;
  pageBg: string;
  ink: string;
  inkSoft: string;
  inkFaint: string;
  accent: string;
  accentBg: string;
  displayFont: string;
  bodyFont: string;
}

export interface StatementPalette {
  id: string;
  name: string;
  blurb: string;
  pageBg: string;
  pitchBgFrom: string;
  pitchBgTo: string;
  pitchBorder: string;
  pitchLabel: string;
  pitchEm: string;
  ink: string;
  inkSoft: string;
  inkFaint: string;
  accent: string;
  displayFont: string;
  bodyFont: string;
  romanColor: string;
}

export const COURIER_PALETTES: CourierPalette[] = [
  {
    id: "sage",
    name: "Sage Linen",
    blurb: "Soft sage + cream. Biophilic 2026 wellness - calm and grounded.",
    railBg: "linear-gradient(170deg, #6b8654 0%, #3d5128 100%)",
    railText: "#f5f0e2",
    railSub: "rgba(245,240,226,0.7)",
    railAccent: "#e8d99a",
    pageBg: "#f5f0e2",
    ink: "#1f1c14",
    inkSoft: "#4f4a3c",
    inkFaint: "#8a8472",
    accent: "#3d5128",
    accentBg: "rgba(107,134,84,0.12)",
    displayFont: "'Cormorant Infant', serif",
    bodyFont: "'Crimson Pro', serif",
  },
  {
    id: "plum",
    name: "Plum Velvet",
    blurb: "Deep plum + bone. Wine-bar luxury, slightly femme.",
    railBg: "linear-gradient(170deg, #5a2a48 0%, #2e1227 100%)",
    railText: "#f5ece6",
    railSub: "rgba(245,236,230,0.75)",
    railAccent: "#e8b9a2",
    pageBg: "#ede5d3",
    ink: "#2a1820",
    inkSoft: "#5a4148",
    inkFaint: "#998578",
    accent: "#5a2a48",
    accentBg: "rgba(90,42,72,0.10)",
    displayFont: "'Cormorant Garamond', serif",
    bodyFont: "'Source Serif 4', serif",
  },
  {
    id: "forest",
    name: "Forest & Pistachio",
    blurb: "Deep forest + bright pistachio. Modern restaurant brand.",
    railBg: "linear-gradient(170deg, #1f3a23 0%, #0e1f12 100%)",
    railText: "#f1ede4",
    railSub: "rgba(241,237,228,0.75)",
    railAccent: "#c8d896",
    pageBg: "#f1ede4",
    ink: "#1a1812",
    inkSoft: "#4f4a3c",
    inkFaint: "#8a8472",
    accent: "#7a8d3e",
    accentBg: "rgba(122,141,62,0.12)",
    displayFont: "'Cormorant Infant', serif",
    bodyFont: "'Source Serif 4', serif",
  },
  {
    id: "charcoal",
    name: "Charcoal & Apricot",
    blurb: "Charcoal + soft apricot. Modern, unisex, slightly editorial.",
    railBg: "linear-gradient(170deg, #2a2622 0%, #14110d 100%)",
    railText: "#f4efe2",
    railSub: "rgba(244,239,226,0.7)",
    railAccent: "#e89478",
    pageBg: "#f4efe2",
    ink: "#1a1812",
    inkSoft: "#4f4a3c",
    inkFaint: "#8a8472",
    accent: "#c97152",
    accentBg: "rgba(201,113,82,0.10)",
    displayFont: "'Cormorant Infant', serif",
    bodyFont: "'Newsreader', serif",
  },
  {
    id: "cobalt",
    name: "Cobalt & Mustard",
    blurb: "Cobalt + mustard. Modernist confidence - Mondrian dialed down.",
    railBg: "linear-gradient(170deg, #243d7a 0%, #0e1942 100%)",
    railText: "#f4efe2",
    railSub: "rgba(244,239,226,0.7)",
    railAccent: "#d6b75a",
    pageBg: "#f4efe2",
    ink: "#0e1942",
    inkSoft: "#3d4870",
    inkFaint: "#7c84a0",
    accent: "#243d7a",
    accentBg: "rgba(36,61,122,0.10)",
    displayFont: "'Cormorant Garamond', serif",
    bodyFont: "'Source Serif 4', serif",
  },
];

export const STATEMENT_PALETTES: StatementPalette[] = [
  {
    id: "lavender",
    name: "Aurora Lavender",
    blurb: "Cream + lavender callout. Soft 2026 wellness, refined.",
    pageBg: "#f7f1e8",
    pitchBgFrom: "#e8dfeb",
    pitchBgTo: "#d8c8df",
    pitchBorder: "#7a4a72",
    pitchLabel: "#7a4a72",
    pitchEm: "#7a4a72",
    ink: "#28202a",
    inkSoft: "#5a4858",
    inkFaint: "#9a8898",
    accent: "#7a4a72",
    displayFont: "'Cormorant Garamond', serif",
    bodyFont: "'Source Serif 4', serif",
    romanColor: "#7a4a72",
  },
  {
    id: "terracotta",
    name: "Terracotta & Buttermilk",
    blurb: "Buttermilk + terracotta callout. Pottery-studio warmth.",
    pageBg: "#f7ecd5",
    pitchBgFrom: "#e8a978",
    pitchBgTo: "#c97152",
    pitchBorder: "#7a3a1f",
    pitchLabel: "#fcebd8",
    pitchEm: "#fff4dc",
    ink: "#2a1f1c",
    inkSoft: "#5a4636",
    inkFaint: "#998572",
    accent: "#7a3a1f",
    displayFont: "'Playfair Display', serif",
    bodyFont: "'Crimson Pro', serif",
    romanColor: "#7a3a1f",
  },
  {
    id: "charcoal-modern",
    name: "Charcoal Modern",
    blurb: "Bone + jet-black callout, terracotta name accent. Aesop-grade.",
    pageBg: "#ede7d6",
    pitchBgFrom: "#262421",
    pitchBgTo: "#14120f",
    pitchBorder: "#c97152",
    pitchLabel: "#c97152",
    pitchEm: "#e8d99a",
    ink: "#1a1812",
    inkSoft: "#4f4a3c",
    inkFaint: "#8a8472",
    accent: "#262421",
    displayFont: "'Cormorant Garamond', serif",
    bodyFont: "'Source Serif 4', serif",
    romanColor: "#c97152",
  },
  {
    id: "seaglass",
    name: "Sea Glass & Coral",
    blurb: "Pale celadon + coral callout. Boutique-hotel brand.",
    pageBg: "#e3eae3",
    pitchBgFrom: "#f4998c",
    pitchBgTo: "#d4675a",
    pitchBorder: "#8a3328",
    pitchLabel: "#fff0e8",
    pitchEm: "#fff7ee",
    ink: "#1f2a26",
    inkSoft: "#4a5e58",
    inkFaint: "#8a9890",
    accent: "#8a3328",
    displayFont: "'Playfair Display', serif",
    bodyFont: "'Source Serif 4', serif",
    romanColor: "#8a3328",
  },
  {
    id: "honey",
    name: "Honey & Black",
    blurb: "Honey paper + jet-black callout. Bakery-chic confidence.",
    pageBg: "#f7e7c4",
    pitchBgFrom: "#1a1812",
    pitchBgTo: "#000000",
    pitchBorder: "#c8a542",
    pitchLabel: "#d6b75a",
    pitchEm: "#e8d99a",
    ink: "#1a1812",
    inkSoft: "#4f4a3c",
    inkFaint: "#8a8472",
    accent: "#1a1812",
    displayFont: "'Bodoni Moda', serif",
    bodyFont: "'Source Serif 4', serif",
    romanColor: "#1a1812",
  },
];

// ─────────────────────────────────────────────────────────────────────────
// Density tokens - drive type sizing + gap density per density level
// ─────────────────────────────────────────────────────────────────────────

interface DensityTokens {
  pad: string;       // outer page padding
  h1Size: number;    // hero name in px
  pitchSize: number; // pitch box body in px
  bodySize: number;  // job bullets, body text
  jobRoleSize: number;
  sectionGap: string;
  jobPad: string;
  bulletGap: string;
}

const DENSITY: Record<NonNullable<ResumeData["density"]>, DensityTokens> = {
  rich: {
    pad: "0.65in 0.7in 0.55in 0.6in",
    h1Size: 96,
    pitchSize: 17,
    bodySize: 14,
    jobRoleSize: 19,
    sectionGap: "0.28in",
    jobPad: "12px 0",
    bulletGap: "4px",
  },
  balanced: {
    pad: "0.6in 0.65in 0.5in 0.55in",
    h1Size: 88,
    pitchSize: 16,
    bodySize: 13,
    jobRoleSize: 18,
    sectionGap: "0.22in",
    jobPad: "10px 0",
    bulletGap: "3px",
  },
  compact: {
    pad: "0.5in 0.6in 0.45in 0.5in",
    h1Size: 76,
    pitchSize: 14.5,
    bodySize: 12,
    jobRoleSize: 16,
    sectionGap: "0.16in",
    jobPad: "7px 0",
    bulletGap: "2px",
  },
};

function densityOf(d: ResumeData["density"]): DensityTokens {
  return DENSITY[d ?? "balanced"];
}

// ─────────────────────────────────────────────────────────────────────────
// Monogram (initials disc)
// ─────────────────────────────────────────────────────────────────────────

function Monogram({
  initials,
  size = 64,
  bg,
  color,
  font = "'Fraunces', serif",
  border,
}: {
  initials: string;
  size?: number;
  bg: string;
  color: string;
  font?: string;
  border?: string;
}): JSX.Element {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: bg,
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: font,
        fontStyle: "italic",
        fontWeight: 400,
        fontSize: size * 0.42,
        letterSpacing: "-0.02em",
        fontVariationSettings: '"opsz" 60, "SOFT" 50',
        flexShrink: 0,
        border: border ?? "none",
      }}
    >
      {initials.toLowerCase()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Courier - sidebar layout
// ─────────────────────────────────────────────────────────────────────────

export function CourierResume({
  data,
  palette,
  className = "sheet",
}: {
  data: ResumeData;
  palette: CourierPalette;
  className?: string;
}): JSX.Element {
  const cls = `courier-${palette.id}-${data.initials.toLowerCase()}`;
  const d = densityOf(data.density);
  return (
    <article className={`${className} ${cls}`}>
      <style>{`
        .${cls} { padding: 0; background: ${palette.pageBg}; color: ${palette.ink}; display: flex; }

        .${cls} .rail {
          width: 2.5in;
          background: ${palette.railBg};
          color: ${palette.railText};
          padding: 0.5in 0.4in 0.5in 0.5in;
          display: flex; flex-direction: column; gap: 22px;
        }
        .${cls} .rail .top { display: flex; flex-direction: column; align-items: flex-start; gap: 14px; }
        .${cls} .rail .top .id {
          font-family: ${palette.displayFont};
          font-style: italic; font-weight: 500;
          font-size: 26px; line-height: 1.05;
          letter-spacing: -0.015em;
          color: ${palette.railText};
        }
        .${cls} .rail .top .role {
          font-family: 'Inter', sans-serif;
          font-size: 9.5px; letter-spacing: 0.32em;
          text-transform: uppercase;
          color: ${palette.railSub}; font-weight: 700;
        }
        .${cls} .rail h3 {
          font-family: 'Inter', sans-serif;
          font-size: 9.5px; letter-spacing: 0.36em;
          text-transform: uppercase; font-weight: 700;
          color: ${palette.railText};
          margin: 0 0 10px 0; padding-bottom: 6px;
          border-bottom: 2px solid ${palette.railText};
        }
        .${cls} .rail .field { padding: 6px 0; border-bottom: 1px dotted ${palette.railSub}; }
        .${cls} .rail .field:last-of-type { border-bottom: 0; }
        .${cls} .rail .field .key {
          display: block;
          font-family: 'Inter', sans-serif;
          font-size: 8.5px; letter-spacing: 0.32em;
          text-transform: uppercase;
          color: ${palette.railSub};
          margin-bottom: 2px; font-weight: 700;
        }
        .${cls} .rail .field .val {
          font-family: ${palette.displayFont};
          font-size: 13.5px;
          color: ${palette.railText};
          font-variant-numeric: tabular-nums;
          word-break: break-word;
        }
        .${cls} .rail .skill-list { list-style: none; padding: 0; margin: 0; }
        .${cls} .rail .skill-list li {
          font-family: ${palette.displayFont};
          font-size: 13px; padding: 5px 0;
          border-bottom: 1px dotted ${palette.railSub};
          color: ${palette.railText};
        }
        .${cls} .rail .skill-list li:last-child { border-bottom: 0; }
        .${cls} .rail .ed-row { padding: 6px 0; border-bottom: 1px dotted ${palette.railSub}; }
        .${cls} .rail .ed-row:last-child { border-bottom: 0; }
        .${cls} .rail .ed-row .yr {
          font-family: 'Inter', sans-serif; font-size: 9px;
          letter-spacing: 0.18em; color: ${palette.railAccent};
          font-variant-numeric: tabular-nums; font-weight: 700;
        }
        .${cls} .rail .ed-row .school {
          font-family: ${palette.displayFont}; font-style: italic;
          font-size: 13px; color: ${palette.railText};
          display: block; line-height: 1.18; margin-top: 2px;
        }
        .${cls} .rail .ed-row .deg {
          font-family: 'Inter', sans-serif; font-size: 10px;
          color: ${palette.railSub};
        }

        .${cls} .main { flex: 1; padding: ${d.pad}; }
        .${cls} .main .kicker {
          font-family: 'Inter', sans-serif;
          font-size: 10px; letter-spacing: 0.36em;
          text-transform: uppercase; color: ${palette.accent};
          font-weight: 700; margin-bottom: 14px;
        }
        .${cls} .main h1 {
          font-family: ${palette.displayFont};
          font-style: italic; font-weight: 400;
          font-size: ${d.h1Size}px; line-height: 0.95;
          letter-spacing: -0.035em;
          margin: 0; color: ${palette.ink};
        }
        .${cls} .main h1 .roman {
          font-style: normal; font-weight: 600;
          color: ${palette.accent};
        }
        .${cls} .main .pitch {
          margin: 0.18in 0 ${d.sectionGap} 0;
          font-family: ${palette.bodyFont};
          font-size: ${d.pitchSize}px; line-height: 1.5;
          color: ${palette.ink};
          padding: 14px 18px;
          background: ${palette.accentBg};
          border-left: 3px solid ${palette.accent};
        }
        .${cls} .main .pitch strong { color: ${palette.accent}; font-weight: 700; }
        .${cls} .main h2 {
          font-family: 'Inter', sans-serif;
          font-size: 10.5px; letter-spacing: 0.36em;
          text-transform: uppercase; font-weight: 800;
          color: ${palette.ink};
          margin: 0 0 12px 0; padding-bottom: 6px;
          border-bottom: 2px solid ${palette.ink};
          display: flex; justify-content: space-between;
        }
        .${cls} .main h2 .ix { color: ${palette.accent}; font-weight: 600; letter-spacing: 0.2em; }
        .${cls} .main .job { padding: ${d.jobPad}; border-top: 1px solid rgba(0,0,0,0.08); }
        .${cls} .main .job:nth-of-type(1) { border-top: 0; padding-top: 0; }
        .${cls} .main .job .head {
          display: flex; justify-content: space-between; align-items: baseline;
          gap: 14px; margin-bottom: 2px;
        }
        .${cls} .main .job .role {
          font-family: ${palette.displayFont};
          font-weight: 600; font-size: ${d.jobRoleSize}px;
          color: ${palette.ink}; letter-spacing: -0.005em;
        }
        .${cls} .main .job .when {
          font-family: 'Inter', sans-serif;
          font-size: 10px; letter-spacing: 0.18em;
          text-transform: uppercase; color: ${palette.inkSoft};
          font-variant-numeric: tabular-nums;
          white-space: nowrap; font-weight: 700;
        }
        .${cls} .main .job .org {
          font-family: ${palette.bodyFont}; font-style: italic;
          font-size: ${d.bodySize - 0.5}px; color: ${palette.inkSoft};
          margin-bottom: 4px;
        }
        .${cls} .main .job ul {
          list-style: none; padding: 0; margin: 0;
          font-family: ${palette.bodyFont};
          font-size: ${d.bodySize}px; line-height: 1.5; color: ${palette.ink};
        }
        .${cls} .main .job ul li { padding-left: 16px; position: relative; margin: ${d.bulletGap} 0; }
        .${cls} .main .job ul li::before {
          content: '◆'; position: absolute; left: 0; top: 4px;
          color: ${palette.accent}; font-size: 7px;
        }
      `}</style>

      <aside className="rail">
        <div className="top">
          <Monogram
            initials={data.initials}
            size={64}
            bg={palette.railAccent}
            color={
              palette.railBg.includes("gradient")
                ? "#1a1812"
                : palette.railBg
            }
          />
          <div>
            <div className="id">
              {data.first} {data.middle ?? ""}
              <br />
              {data.last}.
            </div>
            <div className="role">{data.title}</div>
          </div>
        </div>

        <div>
          <h3>Contact</h3>
          {data.email ? (
            <div className="field">
              <span className="key">Email</span>
              <span className="val">{data.email}</span>
            </div>
          ) : null}
          {data.phone ? (
            <div className="field">
              <span className="key">Phone</span>
              <span className="val">{data.phone}</span>
            </div>
          ) : null}
          {data.city ? (
            <div className="field">
              <span className="key">City</span>
              <span className="val">{data.city}</span>
            </div>
          ) : null}
        </div>

        {data.education.length > 0 ? (
          <div>
            <h3>Education</h3>
            {data.education.map((e, i) => (
              <div className="ed-row" key={i}>
                <span className="yr">{e.year}</span>
                <span className="school">{e.school}</span>
                <span className="deg">{e.degree}</span>
              </div>
            ))}
          </div>
        ) : null}

        {data.skills.length > 0 ? (
          <div>
            <h3>Skills</h3>
            <ul className="skill-list">
              {data.skills.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </aside>

      <main className="main">
        <div className="kicker">Curriculum Vitae · 2026</div>
        <h1>
          {data.first} {data.middle ?? ""}
          <br />
          <span className="roman">{data.last}.</span>
        </h1>
        <p
          className="pitch"
          dangerouslySetInnerHTML={{ __html: emphasizeFirstNumber(data.long_pitch) }}
        />

        <h2>
          <span>Experience</span>
          <span className="ix">{experienceRange(data.experience)}</span>
        </h2>
        {data.experience.map((j, i) => (
          <div className="job" key={i}>
            <div className="head">
              <span className="role">{j.role}</span>
              <span className="when">
                {j.start} - {j.end}
              </span>
            </div>
            <div className="org">
              {j.org}
              {j.city ? `, ${j.city}` : ""}
            </div>
            {j.bullets.length > 0 ? (
              <ul>
                {j.bullets.map((b, k) => (
                  <li key={k}>{b.text}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </main>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Statement - single-column body with shaded callout
// ─────────────────────────────────────────────────────────────────────────

export function StatementResume({
  data,
  palette,
  className = "sheet",
}: {
  data: ResumeData;
  palette: StatementPalette;
  className?: string;
}): JSX.Element {
  const cls = `statement-${palette.id}-${data.initials.toLowerCase()}`;
  const d = densityOf(data.density);
  return (
    <article className={`${className} ${cls}`}>
      <style>{`
        .${cls} { padding: ${d.pad}; background: ${palette.pageBg}; color: ${palette.ink}; font-family: ${palette.bodyFont}; }

        .${cls} .top {
          display: flex; justify-content: space-between; align-items: center;
          font-family: 'Inter', sans-serif;
          font-size: 9.5px; letter-spacing: 0.36em;
          text-transform: uppercase; color: ${palette.inkSoft};
          padding-bottom: 12px;
          border-bottom: 1px solid ${palette.ink};
        }
        .${cls} .top .accent { color: ${palette.accent}; font-weight: 700; }

        .${cls} .hero {
          padding: 0.32in 0 0.18in 0;
          display: grid; grid-template-columns: auto 1fr; gap: 26px;
          align-items: end;
          border-bottom: 1px solid rgba(0,0,0,0.18);
        }
        .${cls} .hero h1 {
          font-family: ${palette.displayFont};
          font-weight: 500; font-style: italic;
          font-size: ${d.h1Size}px; line-height: 0.92;
          letter-spacing: -0.025em;
          margin: 0; color: ${palette.ink};
        }
        .${cls} .hero h1 .roman {
          font-style: normal; font-weight: 700;
          color: ${palette.romanColor};
        }
        .${cls} .hero .role {
          margin-top: 8px;
          font-family: 'Inter', sans-serif;
          font-size: 11.5px; letter-spacing: 0.18em;
          text-transform: uppercase;
          color: ${palette.inkSoft}; font-weight: 600;
        }
        .${cls} .hero .role .dot { color: ${palette.accent}; margin: 0 10px; }

        .${cls} .meta-strip {
          padding: 14px 0;
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px;
          border-bottom: 1px solid ${palette.ink};
          font-family: 'Inter', sans-serif;
          font-size: 11px; color: ${palette.ink};
          font-variant-numeric: tabular-nums;
        }
        .${cls} .meta-strip .key {
          display: block; font-size: 8.5px;
          letter-spacing: 0.36em; text-transform: uppercase;
          color: ${palette.inkFaint}; font-weight: 700;
          margin-bottom: 4px;
        }

        .${cls} .pitch-box {
          margin: 0.22in 0 ${d.sectionGap} 0;
          padding: 22px 26px;
          background: linear-gradient(135deg, ${palette.pitchBgFrom} 0%, ${palette.pitchBgTo} 100%);
          border-left: 4px solid ${palette.pitchBorder};
        }
        .${cls} .pitch-box .label {
          font-family: 'Inter', sans-serif;
          font-size: 9.5px; letter-spacing: 0.36em;
          text-transform: uppercase; color: ${palette.pitchLabel};
          font-weight: 700; margin-bottom: 10px;
        }
        .${cls} .pitch-box .quote {
          font-family: ${palette.displayFont};
          font-style: italic; font-weight: 500;
          font-size: ${d.pitchSize + 5}px; line-height: 1.4;
          color: ${palette.pitchEm}; letter-spacing: -0.005em;
        }
        .${cls} .pitch-box.lavender .quote { color: ${palette.ink}; }
        .${cls} .pitch-box .quote strong {
          font-style: normal; font-weight: 700;
          color: ${palette.pitchEm};
        }
        .${cls} .pitch-box.lavender .quote strong { color: ${palette.accent}; }

        .${cls} h2 {
          font-family: 'Inter', sans-serif;
          font-size: 11px; letter-spacing: 0.36em;
          text-transform: uppercase; font-weight: 800;
          color: ${palette.ink};
          margin: 0.16in 0 0.12in 0; padding-bottom: 6px;
          border-bottom: 2px solid ${palette.ink};
          display: flex; justify-content: space-between;
        }
        .${cls} h2 .ix { color: ${palette.accent}; font-weight: 600; letter-spacing: 0.2em; }

        .${cls} .job { padding: ${d.jobPad}; border-top: 1px solid rgba(0,0,0,0.08); }
        .${cls} .job:nth-of-type(1) { border-top: 0; padding-top: 0; }
        .${cls} .job .head {
          display: flex; justify-content: space-between; align-items: baseline;
          gap: 14px; margin-bottom: 2px;
        }
        .${cls} .job .role {
          font-family: ${palette.displayFont};
          font-style: italic; font-weight: 600;
          font-size: ${d.jobRoleSize + 1}px; color: ${palette.ink};
          letter-spacing: -0.005em;
        }
        .${cls} .job .when {
          font-family: 'Inter', sans-serif;
          font-size: 10.5px; letter-spacing: 0.18em;
          text-transform: uppercase; color: ${palette.inkSoft};
          font-variant-numeric: tabular-nums;
          white-space: nowrap; font-weight: 700;
        }
        .${cls} .job .org {
          font-family: ${palette.bodyFont};
          font-size: ${d.bodySize}px; color: ${palette.inkSoft};
          margin-bottom: 4px;
        }
        .${cls} .job ul {
          list-style: none; padding: 0; margin: 0;
          font-family: ${palette.bodyFont};
          font-size: ${d.bodySize}px; line-height: 1.5; color: ${palette.ink};
        }
        .${cls} .job ul li { padding-left: 16px; position: relative; margin: ${d.bulletGap} 0; }
        .${cls} .job ul li::before {
          content: ''; position: absolute; left: 0; top: 8px;
          width: 8px; height: 1.5px; background: ${palette.accent};
        }

        .${cls} .ed-skills {
          display: grid; grid-template-columns: 1.4fr 1fr; gap: 0.4in;
          margin-top: 0.16in;
        }
        .${cls} .ed-row, .${cls} .skill-row {
          padding: 6px 0;
          border-top: 1px solid rgba(0,0,0,0.08);
          font-family: ${palette.bodyFont};
          font-size: ${d.bodySize - 0.5}px; color: ${palette.ink};
        }
        .${cls} .ed-row:first-of-type, .${cls} .skill-row:first-of-type {
          border-top: 0; padding-top: 0;
        }
        .${cls} .ed-row .yr {
          font-family: 'Inter', sans-serif; font-size: 9.5px;
          letter-spacing: 0.18em; color: ${palette.accent};
          font-variant-numeric: tabular-nums;
          float: right; font-weight: 700;
        }
        .${cls} .ed-row .school { font-weight: 700; }
        .${cls} .ed-row .deg { font-style: italic; color: ${palette.inkSoft}; display: block; font-size: ${d.bodySize - 1}px; }
      `}</style>

      <div className="top">
        <span>
          <span className="accent">●</span> &nbsp; {data.first} {data.middle ?? ""}{" "}
          {data.last}
        </span>
        <span>Curriculum Vitae · 2026</span>
      </div>

      <div className="hero">
        <Monogram
          initials={data.initials}
          size={84}
          bg={palette.accent}
          color={palette.pageBg}
        />
        <div>
          <h1>
            {data.first} <span className="roman">{data.last}.</span>
          </h1>
          <div className="role">
            {data.title.split(/[·,]/).map((part, i, arr) => (
              <span key={i}>
                {part.trim()}
                {i < arr.length - 1 ? <span className="dot">·</span> : null}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="meta-strip">
        {data.email ? (
          <div>
            <span className="key">Email</span>
            {data.email}
          </div>
        ) : (
          <div />
        )}
        {data.phone ? (
          <div>
            <span className="key">Phone</span>
            {data.phone}
          </div>
        ) : (
          <div />
        )}
        <div>
          <span className="key">Status</span>
          Open to work · 2026
        </div>
      </div>

      <div className={`pitch-box ${palette.id === "lavender" ? "lavender" : ""}`}>
        <div className="label">/ What I do</div>
        <div
          className="quote"
          dangerouslySetInnerHTML={{ __html: emphasizeFirstNumber(data.long_pitch) }}
        />
      </div>

      <h2>
        <span>Experience</span>
        <span className="ix">{experienceRange(data.experience)}</span>
      </h2>
      {data.experience.map((j, i) => (
        <div className="job" key={i}>
          <div className="head">
            <span className="role">{j.role}</span>
            <span className="when">
              {j.start} - {j.end}
            </span>
          </div>
          <div className="org">
            {j.org}
            {j.city ? `, ${j.city}` : ""}
          </div>
          {j.bullets.length > 0 ? (
            <ul>
              {j.bullets.map((b, k) => (
                <li key={k}>{b}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}

      {data.education.length > 0 || data.skills.length > 0 ? (
        <div className="ed-skills">
          {data.education.length > 0 ? (
            <div>
              <h2>
                <span>Education</span>
                <span className="ix">/ 02</span>
              </h2>
              {data.education.map((e, i) => (
                <div className="ed-row" key={i}>
                  <span className="yr">{e.year}</span>
                  <span className="school">{e.school}</span>
                  <span className="deg">{e.degree}</span>
                </div>
              ))}
            </div>
          ) : (
            <div />
          )}
          {data.skills.length > 0 ? (
            <div>
              <h2>
                <span>Skills</span>
                <span className="ix">/ 03</span>
              </h2>
              {data.skills.slice(0, 6).map((s, i) => (
                <div className="skill-row" key={i}>
                  {s}
                </div>
              ))}
            </div>
          ) : (
            <div />
          )}
        </div>
      ) : null}
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

/** Bold the first number-token in the pitch ("Six years" / "1,400+ calls") so
 *  the quote box has a clear visual entry point without relying on the LLM
 *  to add markdown. Fallback wraps the first 1-3 word phrase if there's no
 *  obvious number. */
function emphasizeFirstNumber(text: string): string {
  const numMatch = text.match(/\b(\d[\d,]*\+?\s+\w+|\w+\s+years?|\w+\s+months?)\b/i);
  if (numMatch) {
    return text.replace(
      numMatch[0],
      `<strong>${numMatch[0]}</strong>`,
    );
  }
  // Fallback - wrap the first sentence.
  const firstStop = text.search(/[.!?]/);
  if (firstStop > 0) {
    const head = text.slice(0, firstStop);
    const tail = text.slice(firstStop);
    return `<strong>${head}</strong>${tail}`;
  }
  return text;
}

function experienceRange(jobs: ResumeJob[]): string {
  if (jobs.length === 0) return "";
  const first = jobs[jobs.length - 1]?.start ?? "";
  const last = jobs[0]?.end ?? "Present";
  const startYear = first.match(/\d{4}/)?.[0] ?? first;
  const endYear = last.match(/\d{4}/)?.[0] ?? last;
  if (!startYear || !endYear) return "";
  if (startYear === endYear) return startYear;
  return `${startYear} - ${endYear}`;
}
